<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import type { TrainingDetail } from '@/entities/Training';
  import { PositionUserSelector, SimpleLoading } from '@/shared/ui';
  import { TRAINING_EMPTY_VALUE } from '../../lib/trainingEmptyValue';
  import type { useEditHomologationTraining } from '../../model/useEditHomologationTraining';
  import TrainingViewInstructorsList from '../view/TrainingViewInstructorsList.vue';
  import TrainingMinistrantesSection from '../TrainingMinistrantesSection.vue';
  import TrainingNotesField from '../TrainingNotesField.vue';

  const props = defineProps<{
    training: TrainingDetail;
    form: ReturnType<typeof useEditHomologationTraining>;
  }>();

  onMounted(async () => {
    await props.form.load();
  });

  const document = computed(() => props.training.documents[0] ?? null);
  const prefixLabel = computed(() => document.value?.prefix ?? TRAINING_EMPTY_VALUE);
  const titleLabel = computed(() => document.value?.title ?? TRAINING_EMPTY_VALUE);
  const revisionLabel = computed(() =>
    document.value?.revision !== null && document.value?.revision !== undefined
      ? `Rev. ${String(document.value.revision).padStart(2, '0')}`
      : TRAINING_EMPTY_VALUE
  );

  const approverName = computed(() => props.training.approval?.responsible?.name ?? TRAINING_EMPTY_VALUE);
  const approverPositionName = computed(
    () => props.training.approval?.position?.name ?? TRAINING_EMPTY_VALUE
  );
  const approvalDateLabel = computed(() => props.training.approval?.date ?? TRAINING_EMPTY_VALUE);
</script>

<template>
  <div
    v-if="form.loading.value"
    data-testid="edit-homologation-form-loading"
    class="flex w-full justify-center py-12"
  >
    <SimpleLoading />
  </div>

  <div
    v-else
    data-testid="edit-homologation-training-form"
    class="flex flex-col"
  >
    <section
      data-testid="edit-homologation-section-document"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Documento SGQ:
      </h2>
      <div class="flex flex-wrap gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-gray-07">Prefixo</span>
          <span
            data-testid="edit-homologation-document-prefix"
            class="w-fit rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3.5 text-sm text-gray-07"
          >
            {{ prefixLabel }}
          </span>
        </div>
        <div class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-gray-07">Título</span>
          <span
            data-testid="edit-homologation-document-title"
            class="w-fit rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3.5 text-sm text-gray-07"
          >
            {{ titleLabel }}
          </span>
        </div>
        <div class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-gray-07">Revisão</span>
          <span
            data-testid="edit-homologation-document-revision"
            class="w-fit rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3.5 text-sm text-gray-07"
          >
            {{ revisionLabel }}
          </span>
        </div>
      </div>
    </section>

    <section
      data-testid="edit-homologation-section-instructors"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Ministrante
      </h2>
      <TrainingViewInstructorsList :instructors="training.instructors" />
    </section>

    <section
      data-testid="edit-homologation-section-approver"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Responsável pela aprovação do treinamento:
      </h2>
      <div class="flex flex-wrap gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-gray-07">Responsável</span>
          <span
            data-testid="edit-homologation-approver"
            class="w-fit rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3.5 text-sm text-gray-07"
          >
            {{ approverName }}
          </span>
        </div>
        <div class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-gray-07">Cargo</span>
          <span
            data-testid="edit-homologation-approver-position"
            class="w-fit rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3.5 text-sm text-gray-07"
          >
            {{ approverPositionName }}
          </span>
        </div>
        <div class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-gray-07">Data da aprovação</span>
          <span
            data-testid="edit-homologation-approval-date"
            class="w-fit rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3.5 text-sm text-gray-07"
          >
            {{ approvalDateLabel }}
          </span>
        </div>
      </div>
    </section>

    <section
      data-testid="edit-homologation-section-collaborators"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Selecione os colaboradores que deverão passar pelo treinamento:
      </h2>
      <PositionUserSelector
        data-testid="edit-homologation-collaborators-selector"
        :positions="form.collaborators.positions.value"
        :users="form.collaborators.users.value"
        :selected-positions="form.collaborators.selectedPositions.value"
        :selected-users="form.collaborators.selectedUsers.value"
        :positions-selected-count="form.collaborators.positionsSelectedCount.value"
        :users-selected-count="form.collaborators.usersSelectedCount.value"
        @update:selected-positions="form.collaborators.selectedPositions.value = $event"
        @update:selected-users="form.collaborators.selectedUsers.value = $event"
      />
    </section>

    <TrainingMinistrantesSection
      :ministrantes="form.newMinistrantes"
      :members="form.members.value"
      :member-options="form.memberOptions.value"
    />

    <TrainingNotesField
      :model-value="form.notes.value"
      testid="edit-homologation-notes-input"
      @update:model-value="form.notes.value = $event"
    />
  </div>
</template>
